import React from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { observer } from 'mobx-react-lite';
import { ApplicationSdk } from '../../../core/sdk';
import { InputText } from 'primereact/inputtext';
import { PickList } from 'primereact/picklist';
import { MultiSelect } from 'primereact/multiselect';
import { Tooltip } from 'primereact/tooltip';
import { ProgressSpinner } from 'primereact/progressspinner';
import 'react-image-crop/dist/ReactCrop.css';

const ManagerObjectList = observer(({ store }: { store: any }) => {
    const itemTemplate = (item: any) => {
        return (
            <div className="flex flex-wrap p-2 align-items-center gap-3">
                <div className="flex-1 flex flex-column gap-2">
                    <span style={{ textAlign: 'left' }}>{item.name}</span>
                </div>
            </div>
        );
    };

    const onChange = (event: any) => {
        store.updateManagerAssignment(event.source, event.target);
    };

    return (
        <div style={{ marginTop: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                {!store.isAvailableForAll ? (
                    <PickList
                        dataKey="id"
                        source={store.unassignedObjectsArray}
                        target={store.assignedObjectsArray}
                        onChange={onChange}
                        itemTemplate={itemTemplate}
                        filter
                        showTargetControls={false}
                        showSourceControls={false}
                        filterBy="name"
                        breakpoint="1280px"
                        sourceHeader="Verfügbare Objekte"
                        targetHeader="Zugewiesene Objekte"
                        sourceStyle={{ height: '20rem' }}
                        targetStyle={{ height: '20rem' }}
                        sourceFilterPlaceholder="Search by name"
                        targetFilterPlaceholder="Search by name"
                    />
                ) : null}
            </div>
        </div>
    );
});

const ManagerName = observer(({ store }: { store: any }) => {
    const handleInputChangeFirstname = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value.length <= 40) {
            store.firstname = e.target.value;
        }
    };
    const handleInputChangeLastname = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value.length <= 40) {
            store.lastname = e.target.value;
        }
    };

    return (
        <>
            <div style={{ marginBottom: '1rem', width: '100%' }}>
                <label
                    htmlFor="firstname"
                    style={{ display: 'block', marginBottom: '0.5rem' }}
                >
                    Vorname
                </label>
                <InputText
                    id="firstname"
                    placeholder="Vorname"
                    value={store.firstname}
                    onChange={handleInputChangeFirstname}
                    invalid={!store.validFirstname}
                    style={{
                        width: '100%',
                    }}
                    data-testid={'manager-input-firstname'}
                    data-pr-tooltip="Der Vorname muss mindestens 3 Zeichen lang sein."
                    data-pr-position="top"
                    data-pr-disabled={store.validFirstname}
                />
                <Tooltip target="#firstname" />
            </div>
            <div style={{ marginBottom: '1rem', width: '100%' }}>
                <label
                    htmlFor="lastname"
                    style={{ display: 'block', marginBottom: '0.5rem' }}
                >
                    Nachname
                </label>
                <InputText
                    id="lastname"
                    placeholder="Nachname"
                    value={store.lastname}
                    data-testid={'manager-input-lastname'}
                    onChange={handleInputChangeLastname}
                    invalid={!store.validLastname}
                    style={{
                        width: '100%',
                    }}
                    data-pr-tooltip="Der Nachname muss mindestens 3 Zeichen lang sein."
                    data-pr-position="top"
                    data-pr-disabled={store.validLastname}
                />
                <Tooltip target="#lastname" />
            </div>
        </>
    );
});

const ManagerMail = observer(({ store }: { store: any }) => {
    const handleInputChangeMail = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value.length <= 40) {
            store.mail = e.target.value;
        }
    };

    return (
        <>
            <div style={{ marginBottom: '1rem', width: '100%' }}>
                <label
                    htmlFor="mail"
                    style={{ display: 'block', marginBottom: '0.5rem' }}
                >
                    E-Mail
                </label>
                <InputText
                    id="mail"
                    placeholder="E-Mail"
                    value={store.mail}
                    invalid={!store.validMail}
                    onChange={handleInputChangeMail}
                    style={{ width: '100%' }}
                    data-testid={'manager-input-email'}
                    data-pr-tooltip="Du musst eine gültige E-Mail-Adresse eingeben."
                    data-pr-position="top"
                    data-pr-disabled={store.validMail}
                />
                <Tooltip target="#mail" />
            </div>
        </>
    );
});

const ManagerRights = observer(({ store }: { store: any }) => {
    return (
        <>
            <div style={{ marginBottom: '1rem', width: '100%' }}>
                <label
                    htmlFor="mail"
                    style={{ display: 'block', marginBottom: '0.5rem' }}
                >
                    Rechte
                </label>
                <MultiSelect
                    id="rights"
                    value={store.selectedRights}
                    onChange={(e: any) => store.onRightsChange(e.value)}
                    options={store.allAvailableRights}
                    optionLabel="label"
                    data-testid={'manager-input-permissions'}
                    placeholder="Rechte wählen"
                    style={{ width: '100%', maxWidth: '100%' }}
                    selectAllLabel="Alle Rechte zuweisen"
                />
            </div>
        </>
    );
});

const ManagerDialog = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    const store = sdk.currentDialog;
    const isValid = store?.isValid || false;
    const handleManagerSave = async () => {
        try {
            await store.onSaveManager();
            // Erfolgreich => Schließe nun die Ansicht
            sdk.closeDialog({
                event: 'SAVED',
            });
            sdk.showSuccessToast('Objektleiter erfolgreich gespeichert');
        } catch (error: any) {
            sdk.showErrorToast(error?.message || 'Unbekannter Fehler');
        }
    };
    return (
        <Dialog
            visible={!!sdk.currentDialog}
            modal
            header={store?.header || ''}
            footer={() => (
                <div>
                    <Button
                        label={store.saveButton}
                        icon="pi pi-check"
                        disabled={!isValid}
                        onClick={handleManagerSave}
                        data-testid={'manager-button-save'}
                    />
                </div>
            )}
            style={{ width: '80rem', height: '50rem' }}
            onHide={() => sdk.closeDialog()}
            data-testid={'manager-dialog'}
        >
            {store.managerDataLoading ? (
                // Show loading spinner when managerDataLoading is true
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                    }}
                >
                    <ProgressSpinner />
                </div>
            ) : (
                <div className="dialog-content">
                    <div className={'left-column'}>
                        <div className={'card'}>
                            <ManagerName store={store} />
                            <ManagerMail store={store} />
                            <ManagerRights store={store} />
                        </div>
                    </div>
                    <div className={'right-column'}>
                        <div className={'card'}>
                            <ManagerObjectList store={store} />
                        </div>
                    </div>
                </div>
            )}
        </Dialog>
    );
});

export default ManagerDialog;
